/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { communicationAttachmentFromFile, communicationAttachmentToFile } from '@gh/builder-shared';
import {
  ContactDeliveryMethod,
  ContactSupplierListItemDto,
  JeOrderGeneratedDto,
  JobCommunicationAttachmentDto,
  JobOrderDelivery,
  JobOrderDetailsDto,
  JobOrderMethod,
  jobOrderMethodFromDeliveryMethod,
  jobOrderMethodToDeliveryMethod,
} from '@gh/builder-shared-data';
import { FileListItem } from '@gh/common-internal-shared';
import { AutocompleteItem, ID, WithVersion } from '@gh/common-shared-data';
import { isNotNil } from '@gh/core-util';
import { map } from 'lodash';

export type JobOrderSupplier = AutocompleteItem & { isPreferred?: boolean; orderBy: ContactDeliveryMethod };

export type JobOrderDetailsMasterInfo = {
  jobId: ID;
  isDpp: boolean;
  isAuthorized: boolean;
  isFinalized: boolean;
  isNotProceeding: boolean;
  jobDocsFolderId: ID;
};

export type JobOrderDetails = WithVersion<{
  orderId: ID;
  orderNumber: number;
  costCenter: AutocompleteItem;
  deliveryDate?: Date;
  deliveryEnteredDate?: Date;
  deliveryScheduleDate?: Date;
  producedDate?: Date;
  supplier?: JobOrderSupplier;
  attachments: FileListItem[];
  method?: JobOrderMethod;
  delivery: JobOrderDelivery;
  amount: number;
  allowReprint: boolean;
  unlocked?: boolean;
}>;

export type JobOrderClonedOrder = { costCenterCode: string; orderNumber: string };

export const jobOrderDetailsFromGeneratedDto = (order: Partial<JobOrderDetails>,
                                                dto: JeOrderGeneratedDto): JobOrderDetails => ({
  orderId: dto.orderId,
  orderNumber: order.orderNumber!,
  costCenter: order.costCenter!,
  supplier: dto.supplierId
    ? { id: dto.supplierId, name: dto.supplierName, orderBy: jobOrderMethodToDeliveryMethod(dto.method) }
    : void 0,
  allowReprint: false,
  method: dto.method,
  delivery: JobOrderDelivery.Schedule,
  attachments: [],
  amount: 0,
});

export type JobFileListItem = FileListItem & { isJobOrder: boolean };

export const jobCommunicationAttachmentToFile = (attachment: JobCommunicationAttachmentDto): JobFileListItem => ({
  ...communicationAttachmentToFile(attachment),
  isJobOrder: attachment.isJobOrder,
});

export const jobCommunicationAttachmentFromFile = (file: JobFileListItem): JobCommunicationAttachmentDto => ({
  ...communicationAttachmentFromFile(file),
  isJobOrder: file.isJobOrder,
});

export const jobOrderDetailsToDto = (model: JobOrderDetails): JobOrderDetailsDto => ({
  orderId: model.orderId,
  orderNumber: model.orderNumber,
  costCenterId: model.costCenter.id,
  deliveryDate: model.delivery === JobOrderDelivery.Entered ? model.deliveryDate : void 0,
  deliveryScheduleDate: model.deliveryScheduleDate,
  producedDate: model.producedDate,
  supplierId: model.supplier && model.supplier.id,
  supplierName: model.supplier && model.supplier.name,
  supplierOrderBy: model.supplier && jobOrderMethodFromDeliveryMethod(model.supplier.orderBy),
  method: model.method,
  attachments: map(model.attachments, jobCommunicationAttachmentFromFile),
  delivery: model.delivery,
  amount: model.amount,
  allowReprint: model.allowReprint,
  version: model.version,
});

export const jobOrderDetailsFromDto = (dto: JobOrderDetailsDto, costCenterCode: string): JobOrderDetails => ({
  orderId: dto.orderId,
  orderNumber: dto.orderNumber,
  costCenter: { id: dto.costCenterId, name: costCenterCode },
  deliveryDate: dto.delivery === JobOrderDelivery.Entered ? dto.deliveryDate : dto.deliveryScheduleDate,
  deliveryEnteredDate: dto.deliveryDate,
  deliveryScheduleDate: dto.deliveryScheduleDate,
  producedDate: dto.producedDate,
  supplier: dto.supplierId && dto.supplierName && dto.supplierOrderBy
    ? { id: dto.supplierId, name: dto.supplierName, orderBy: jobOrderMethodToDeliveryMethod(dto.supplierOrderBy) }
    : void 0,
  attachments: map(dto.attachments, jobCommunicationAttachmentToFile),
  method: dto.method,
  delivery: dto.delivery,
  amount: dto.amount,
  allowReprint: dto.allowReprint,
  version: dto.version,
});

export const jobOrderSupplierFromContactDto = ({ contactId, name, orderBy }: ContactSupplierListItemDto): JobOrderSupplier =>
  ({ id: contactId, name, orderBy, isPreferred: false });

export const isJobOrderProduced = (job: JobOrderDetails) => isNotNil(job.producedDate);
